import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { CoreBox, CoreTypography } from '@youscience/core';
import { Roles } from '@youscience/commonapp-service-common';

import { BREADCRUMBS_PAGE_TEXT, BREADCRUMBS_NAVIGATION_DATA } from '@constants/breadcrumbs';
import { EDC_HASH } from '@constants';

import { Breadcrumbs } from '@components/Breadcrumbs';
import ActionButton from '@components/ActionButton';

import useCommonAppAdminStore from '@store/CommonAppAdminStore';

import ApplicationRequestModal from '@pages/RecommenderSpace/components/ApplicationRequestModal';

import { useUserProfile } from '@hooks/useUserProfile';

import CommonAppLogo from '@images/common-app-logo-medium.svg?react';

import UserRolesTab from './components/UserRolesTab';

import { sxStyles, StyledHeadSectionInner, StyledCommonAppWrap } from './RecommenderSpace.styles';

export const RecommenderSpace = () => {
  const location = useLocation();
  const [isOpenApplicationRequest, setIsOpenApplicationRequest] = useState(false);
  const [hasInitialFetch, setHasInitialFetch] = useState(false);

  const { firstName, lastName, phone, email, organizationName = '' } = useUserProfile();
  const { getRoles, userRoles } = useCommonAppAdminStore((store) => store);

  const handleOpenApplicationRequest = () => {
    setIsOpenApplicationRequest(true);
  };

  const handleCloseApplicationRequest = () => {
    setIsOpenApplicationRequest(false);
  };

  useEffect(() => {
    const init = async () => {
      await getRoles();
      setHasInitialFetch(true);
    };

    void init();
  }, [getRoles]);

  useEffect(() => {
    if (hasInitialFetch) {
      const hasRequiredRole = userRoles?.some((role) => role === Roles.COUNSELOR || role === Roles.TEACHER) ?? false;

      if (!phone || !email || !firstName || !lastName || !hasRequiredRole) {
        setIsOpenApplicationRequest(true);
      }
    }
  }, [hasInitialFetch, userRoles, phone, email, firstName, lastName]);

  return (
    <>
      {isOpenApplicationRequest ? (
        <ApplicationRequestModal
          data-testid="application-request-modal"
          isOpenApplicationRequestModal={isOpenApplicationRequest}
          onCloseApplicationRequestModal={handleCloseApplicationRequest}
        />
      ) : null}

      <StyledHeadSectionInner>
        <Breadcrumbs
          links={BREADCRUMBS_NAVIGATION_DATA[location.hash] || BREADCRUMBS_NAVIGATION_DATA[EDC_HASH]}
          currentPage={BREADCRUMBS_PAGE_TEXT.RECOMMENDER_WORKSPACE}
        />

        <CoreTypography variant="overline" component="h6">
          {organizationName}
        </CoreTypography>

        <StyledCommonAppWrap>
          <CommonAppLogo />

          <CoreBox>
            <CoreTypography sx={sxStyles.commonAppTitle} variant="h4" component="h3">
              Common App applicants
            </CoreTypography>

            <CoreTypography sx={sxStyles.commonAppSubtitle}>
              Manage Common App application requests including reports, transcripts, and recommendations.
            </CoreTypography>
          </CoreBox>
        </StyledCommonAppWrap>

        <ActionButton
          data-testid="manageRecommenderRoles"
          sx={sxStyles.manageRolesBtn}
          variant="outlined"
          onClick={handleOpenApplicationRequest}
        >
          Manage recommender roles
        </ActionButton>
      </StyledHeadSectionInner>

      <UserRolesTab />
    </>
  );
};
